package uz.mint.frontend.api

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import uz.mint.frontend.i18n.Locale

/** What the storefront knows about one page of the catalogue at any moment. */
sealed interface CatalogState {
    data object Loading : CatalogState

    /**
     * @param refreshing a newer request is in flight and [page] is the previous result. Kept on
     *   screen so paging or switching tabs dims the grid instead of blanking it — a flash of
     *   "Загружаем…" on every page turn reads as a broken page.
     */
    data class Loaded(val page: PlanPage, val refreshing: Boolean = false) : CatalogState

    data class Failed(val message: String) : CatalogState
}

/**
 * Loads one page of the catalogue and reloads whenever the query changes.
 *
 * Each section runs its own instance rather than sharing one hoisted fetch: the tariff preview,
 * the full listing and the hero's finder ask the server genuinely different questions now that
 * paging, category and search are server-side, so a single shared result cannot serve them.
 *
 * @param debounceMs delay before firing, restarted by every change. Non-zero for
 *   search-as-you-type, where otherwise every keystroke is a request.
 * @return the state, plus a retry that re-runs the current query.
 */
@Composable
fun rememberPlanPage(
    locale: Locale,
    category: PlanCategory? = null,
    page: Int = 0,
    size: Int = CatalogApi.DEFAULT_PAGE_SIZE,
    query: String = "",
    debounceMs: Long = 0,
    destination: Destination? = null,
): Pair<CatalogState, () -> Unit> {
    var attempt by remember { mutableStateOf(0) }
    var state by remember { mutableStateOf<CatalogState>(CatalogState.Loading) }
    var firstLoad by remember { mutableStateOf(true) }

    // Keyed on the destination's id rather than the object: `Destination` is a data class, so
    // equality already works, but the id is what actually changes the request and keying on it
    // keeps a re-fetch from firing when only the localised name was rebuilt.
    LaunchedEffect(locale, category, page, size, query, destination?.id, attempt) {
        state = when (val current = state) {
            is CatalogState.Loaded -> current.copy(refreshing = true)
            else -> CatalogState.Loading
        }

        // Cancelled and restarted by the next change, which is what makes this a debounce.
        //
        // **Never on the first run.** A debounce exists to swallow keystrokes, and there have
        // been none before the page has drawn once — waiting is pure delay. It cost twice over:
        // 280 ms before the catalogue could even ask for its first cards, and, because the static
        // export snapshots on its own settle heuristic, long enough for /plans to lose that race
        // on every locale and export with "Загружаем тарифы…" baked in where the grid should be.
        if (debounceMs > 0 && !firstLoad) delay(debounceMs)
        firstLoad = false

        state = try {
            CatalogState.Loaded(CatalogApi.fetchPlans(locale, category, page, size, query, destination))
        } catch (e: Throwable) {
            // Static export snapshots from a headless browser that may not reach the backend,
            // so a failure can be baked into the exported HTML. Harmless: Kobweb re-runs this
            // composition on load and the real result replaces it.
            CatalogState.Failed(e.message ?: "catalog unavailable")
        }
    }

    return state to { attempt++ }
}

/** What the checkout dialog knows about the plan it was opened for. */
sealed interface PlanDetailState {
    data object Loading : PlanDetailState
    data class Loaded(val detail: PlanDetail) : PlanDetailState
    data class Failed(val message: String) : PlanDetailState
}

/**
 * Loads one plan's detail and payment options.
 *
 * Deliberately not folded into the list fetch: building a Payme link costs a base64 encode and
 * the merchant config, which is wasted work for two dozen cards when only the opened one gets
 * paid for. It also re-reads the price at the moment of checkout rather than trusting whatever
 * the grid happened to be rendered with.
 */
@Composable
fun rememberPlanDetail(locale: Locale, packageCode: String): Pair<PlanDetailState, () -> Unit> {
    var attempt by remember { mutableStateOf(0) }
    var state by remember { mutableStateOf<PlanDetailState>(PlanDetailState.Loading) }

    LaunchedEffect(locale, packageCode, attempt) {
        state = PlanDetailState.Loading
        state = try {
            PlanDetailState.Loaded(CatalogApi.fetchPlanDetail(locale, packageCode))
        } catch (e: Throwable) {
            PlanDetailState.Failed(e.message ?: "plan unavailable")
        }
    }

    return state to { attempt++ }
}
